import { AlertTriangle, CalendarClock, Lock, Target } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { cheminPlan, useActionsPlan, usePlan, useSituationsPlan, useStructures } from '@/api/hooks';
import { LIBELLE_STATUT_PLAN, type ElementPlan, type FiltresPlan, type StatutPlan } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, dateFr, montant, nombre, pct } from '@/lib/utils';

const VARIANTE_STATUT: Record<StatutPlan, 'default' | 'secondary' | 'destructive' | 'outline'> = { PREVUE: 'outline', NON_DEMARREE: 'outline', EN_COURS: 'secondary', TERMINEE: 'default', SUSPENDUE: 'destructive', ANNULEE: 'destructive' };
const PROFILS_FONDS = ['CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'ADMINISTRATEUR'];
const PROFILS_CLOTURE = ['CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'ADMINISTRATEUR'];

export const StatutPlanBadge = ({ statut }: { statut: StatutPlan }) => <Badge variant={VARIANTE_STATUT[statut]}>{LIBELLE_STATUT_PLAN[statut]}</Badge>;

/** Barre d'avancement physique (0 à 100) ; « non renseigné » quand aucune mise à jour validée. */
export function Avancement({ valeur, petite = false }: { valeur: number | null; petite?: boolean }) {
  if (valeur === null) return <span className="text-xs text-muted-foreground">non renseigné</span>;
  return (
    <div className={cn('flex items-center gap-2', petite ? 'min-w-28' : 'min-w-36')}>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', valeur >= 100 ? 'bg-emerald-600' : valeur >= 50 ? 'bg-sky-600' : 'bg-amber-500')} style={{ width: `${Math.min(100, valeur)}%` }} />
      </div>
      <span className="num w-12 text-right text-xs font-medium">{pct(valeur, 0)}</span>
    </div>
  );
}

const periodes = (): string[] => {
  const out: string[] = [];
  const d = new Date();
  for (let i = 1; i <= 14; i++) {
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    out.push(x.toISOString().slice(0, 7));
  }
  return out;
};

function SituationFigee({ structure, peutFiger }: { structure?: string; peutFiger: boolean }) {
  const liste = periodes();
  const [periode, setPeriode] = useState(liste[0]!);
  const { data, isLoading, error } = useSituationsPlan(periode, structure);
  const actions = useActionsPlan();
  const figer = async () => {
    try {
      const r = await actions.figer.mutateAsync(periode);
      toast.success(`Situation ${r.periode} figée : ${r.figees} sous-activité(s).`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Clôture impossible.');
    }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>Situation figée à la fin d’un mois</CardTitle>
            <CardDescription>État du plan reconstitué à la clôture mensuelle (statut, avancement, retard, exécution) ; figé automatiquement le 1er de chaque mois.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <SelectNatif value={periode} onChange={(e) => setPeriode(e.target.value)} className="w-32">
              {liste.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </SelectNatif>
            {peutFiger && (
              <Button size="sm" variant="outline" disabled={actions.figer.isPending} onClick={figer}>
                <Lock className="size-4" /> Figer maintenant
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && !data ? (
          <Chargement />
        ) : error || !data ? (
          <ErreurBloc erreur={error} />
        ) : !data.lignes.length ? (
          <Vide message={`Aucune situation figée pour ${periode} sur ce périmètre.`} />
        ) : (
          <>
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Kpi label="Avancement du plan" valeur={pct(data.synthese.avancement, 0)} detail={`couverture ${pct(data.synthese.couverture, 0)} · ${data.synthese.renseignees}/${data.synthese.total}`} />
              <Kpi label="Terminées · en cours" valeur={`${data.synthese.parStatut.TERMINEE} · ${data.synthese.parStatut.EN_COURS}`} detail={`${data.synthese.parStatut.PREVUE + data.synthese.parStatut.NON_DEMARREE} non démarrée(s)`} />
              <Kpi label="En retard" valeur={data.synthese.retards} accent={data.synthese.retards ? 'alerte' : undefined} />
              <Kpi label="Payé / budget" valeur={montant(data.synthese.paye)} detail={`sur ${montant(data.synthese.budgetActualise)}`} />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sous-activité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Avancement</TableHead>
                  <TableHead className="text-right">Retard (j)</TableHead>
                  <TableHead className="text-right">Payé</TableHead>
                  <TableHead className="text-right">Exécution</TableHead>
                  <TableHead className="text-right">Écart</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lignes.map((l) => (
                  <TableRow key={l.ligneId}>
                    <TableCell>
                      <Link to={`/plan/${l.ligneId}`} className="font-medium hover:underline">
                        {l.code}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {l.structureCode} · {l.libelle}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatutPlanBadge statut={l.statut} />
                    </TableCell>
                    <TableCell>
                      <Avancement valeur={l.avancement} petite />
                    </TableCell>
                    <TableCell className={cn('num text-right', l.retardJours > 0 && 'font-medium text-red-700')}>{l.retardJours || '—'}</TableCell>
                    <TableCell className="num text-right">{nombre(l.paye)}</TableCell>
                    <TableCell className="num text-right">{pct(l.tauxExecution, 0)}</TableCell>
                    <TableCell className="num text-right">{l.ecart === null ? '—' : `${l.ecart > 0 ? '+' : ''}${nombre(l.ecart)}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Plan opérationnel : sous-activités du périmètre avec statut, avancement physique, exécution financière, retard et fraîcheur. */
export function PlanListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const fonds = u.profils.some((p) => PROFILS_FONDS.includes(p));
  const [params, setParams] = useSearchParams();
  const an = new Date().getFullYear();
  const filtres = useMemo<FiltresPlan>(
    () => ({
      exercice: Number(params.get('exercice')) || an,
      structure: params.get('structure') ?? undefined,
      activite: params.get('activite') ?? undefined,
      responsable: params.get('responsable') ?? undefined,
      statut: (params.get('statut') as StatutPlan | null) ?? undefined,
      retard: params.get('retard') === '1',
      aMettreAJour: params.get('aMettreAJour') === '1',
      enAttente: params.get('enAttente') === '1',
      aParametrer: params.get('aParametrer') === '1',
      ponderation: params.get('ponderation') === 'BUDGET' ? 'BUDGET' : 'EGALE',
    }),
    [params, an],
  );
  const [onglet, setOnglet] = useState<'liste' | 'situations'>(params.get('onglet') === 'situations' ? 'situations' : 'liste');
  const structures = useStructures();
  const { data, isLoading, error, isFetching } = usePlan(filtres);
  const maj = (patch: Partial<Record<keyof FiltresPlan, string | boolean | number | undefined>>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '' || v === false) p.delete(k);
      else p.set(k, v === true ? '1' : String(v));
    }
    setParams(p, { replace: true });
  };
  const bascule = (cle: 'retard' | 'aMettreAJour' | 'enAttente' | 'aParametrer', libelle: string) => (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={Boolean(filtres[cle])} onCheckedChange={(c) => maj({ [cle]: c === true })} />
      {libelle}
    </label>
  );
  const s = data?.synthese;
  const groupes = useMemo(() => {
    const out = new Map<string, { structureCode: string; structureLibelle: string; activites: Map<string, { code: string | null; libelle: string | null; lignes: ElementPlan[] }> }>();
    for (const l of data?.lignes ?? []) {
      const g = out.get(l.structureId) ?? { structureCode: l.structureCode, structureLibelle: l.structureLibelle, activites: new Map() };
      const a = g.activites.get(l.activiteCode ?? '') ?? { code: l.activiteCode, libelle: l.activiteLibelle, lignes: [] };
      a.lignes.push(l);
      g.activites.set(l.activiteCode ?? '', a);
      out.set(l.structureId, g);
    }
    return [...out.entries()];
  }, [data]);
  const activiteDe = (structureCode: string, code: string | null) => s?.structures.find((x) => x.structureCode === structureCode)?.activites.find((a) => a.activiteCode === code);

  return (
    <div className="space-y-5">
      <PageHeader
        titre={`Plan opérationnel ${filtres.exercice}`}
        sousTitre={
          <span>
            Sous-activités du PTBA : avancement physique mesuré par indicateurs et jalons validés, exécution financière des modules existants, jamais déduite d’un TDR, d’un FED ou d’un paiement.
            {isFetching && <span className="ml-2 text-xs">(actualisation…)</span>}
          </span>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/budget">Suivi budgétaire</Link>
            </Button>
            <BoutonExcel chemin={`/plan/liste.xlsx${cheminPlan(filtres)}`} nom={`plan-operationnel-${filtres.exercice}.xlsx`} taille="default" />
          </>
        }
      />
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3 rounded-lg border bg-card px-4 py-3">
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Exercice</Label>
          <SelectNatif value={filtres.exercice} onChange={(e) => maj({ exercice: Number(e.target.value) })} className="w-28">
            {[an - 1, an, an + 1, an + 2].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </SelectNatif>
        </div>
        {fonds && (
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Entité</Label>
            <SelectNatif value={filtres.structure ?? ''} onChange={(e) => maj({ structure: e.target.value || undefined, activite: undefined })} className="w-56">
              <option value="">Tout le Fonds</option>
              {(structures.data ?? []).map((x) => (
                <option key={x.code} value={x.code}>
                  {x.code} · {x.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Statut</Label>
          <SelectNatif value={filtres.statut ?? ''} onChange={(e) => maj({ statut: e.target.value || undefined })} className="w-40">
            <option value="">Tous</option>
            {(Object.keys(LIBELLE_STATUT_PLAN) as StatutPlan[]).map((x) => (
              <option key={x} value={x}>
                {LIBELLE_STATUT_PLAN[x]}
              </option>
            ))}
          </SelectNatif>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Pondération</Label>
          <SelectNatif value={filtres.ponderation ?? 'EGALE'} onChange={(e) => maj({ ponderation: e.target.value === 'BUDGET' ? 'BUDGET' : undefined })} className="w-44">
            <option value="EGALE">Égale (référence)</option>
            <option value="BUDGET">Par le budget (vue alternative)</option>
          </SelectNatif>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-1">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={filtres.responsable === 'moi'} onCheckedChange={(c) => maj({ responsable: c === true ? 'moi' : undefined })} />
            Mes sous-activités
          </label>
          {bascule('aMettreAJour', 'À mettre à jour')}
          {bascule('enAttente', 'En attente de validation')}
          {bascule('retard', 'En retard')}
          {bascule('aParametrer', 'À paramétrer')}
        </div>
      </div>

      <Tabs value={onglet} onValueChange={(v) => (setOnglet(v as 'liste' | 'situations'), maj({ onglet: v === 'situations' ? 'situations' : undefined } as Record<string, string | undefined>))}>
        <TabsList>
          <TabsTrigger value="liste">Suivi en cours</TabsTrigger>
          <TabsTrigger value="situations">Situations figées</TabsTrigger>
        </TabsList>
      </Tabs>

      {onglet === 'situations' ? (
        <SituationFigee structure={filtres.structure} peutFiger={u.profils.some((p) => PROFILS_CLOTURE.includes(p))} />
      ) : isLoading && !data ? (
        <Chargement />
      ) : error || !data || !s ? (
        <ErreurBloc erreur={error} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Avancement du plan" valeur={pct(s.total.avancement, 0)} detail={`${s.ponderation === 'BUDGET' ? 'pondéré par le budget' : 'pondération égale'} · couverture ${pct(s.total.couverture, 0)} (${s.total.renseignees}/${s.total.total})`} />
            <Kpi label="En cours" valeur={s.total.parStatut.EN_COURS} detail={`${s.total.parStatut.TERMINEE} terminée(s) · ${s.total.parStatut.PREVUE + s.total.parStatut.NON_DEMARREE} non démarrée(s) · ${s.total.parStatut.SUSPENDUE + s.total.parStatut.ANNULEE} suspendue(s) ou annulée(s)`} />
            <Kpi label="En retard" valeur={s.total.retards} accent={s.total.retards ? 'alerte' : undefined} detail="fin dépassée, jalon échu ou cible périodique" />
            <Kpi label="À mettre à jour" valeur={s.total.aMettreAJour} accent={s.total.aMettreAJour ? 'alerte' : undefined} detail="échéance de la fréquence atteinte" />
            <Kpi label="En attente de validation" valeur={s.total.enAttente} detail="mises à jour soumises" />
            <Kpi label="Exécution financière" valeur={pct(s.total.tauxExecution, 0)} detail={`${montant(s.total.paye)} payés · ${s.total.aExaminer} écart(s) à examiner`} />
          </div>
          {!data.lignes.length ? (
            <Vide message="Aucune sous-activité sur ce périmètre avec ces filtres." />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Sous-activités</CardTitle>
                <CardDescription>Par entité et par activité (avancement consolidé) ; cliquer sur une sous-activité ouvre sa fiche de suivi.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sous-activité</TableHead>
                      <TableHead>Responsable</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Avancement</TableHead>
                      <TableHead className="text-right">Exécution</TableHead>
                      <TableHead>Suivi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupes.map(([structureId, g]) => (
                      <GroupeStructure key={structureId} g={g} activiteDe={activiteDe} multi={groupes.length > 1} />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function GroupeStructure({ g, activiteDe, multi }: { g: { structureCode: string; structureLibelle: string; activites: Map<string, { code: string | null; libelle: string | null; lignes: ElementPlan[] }> }; activiteDe: (s: string, c: string | null) => { avancement: number | null; couverture: number; renseignees: number; total: number } | undefined; multi: boolean }) {
  return (
    <>
      {multi && (
        <TableRow className="bg-muted/60 hover:bg-muted/60">
          <TableCell colSpan={6} className="font-semibold">
            {g.structureCode} · {g.structureLibelle}
          </TableCell>
        </TableRow>
      )}
      {[...g.activites.values()].map((a) => {
        const c = activiteDe(g.structureCode, a.code);
        return (
          <GroupeActivite key={`${g.structureCode}-${a.code ?? ''}`} a={a} c={c} />
        );
      })}
    </>
  );
}

function GroupeActivite({ a, c }: { a: { code: string | null; libelle: string | null; lignes: ElementPlan[] }; c: { avancement: number | null; couverture: number; renseignees: number; total: number } | undefined }) {
  return (
    <>
      <TableRow className="bg-muted/30 hover:bg-muted/30">
        <TableCell colSpan={3} className="text-sm">
          <span className="font-medium">{a.libelle ?? 'Activité non classée'}</span>
          {a.code && <span className="ml-2 font-mono text-xs text-muted-foreground">{a.code}</span>}
        </TableCell>
        <TableCell>{c && <Avancement valeur={c.avancement} petite />}</TableCell>
        <TableCell colSpan={2} className="text-xs text-muted-foreground">{c ? `couverture ${pct(c.couverture, 0)} (${c.renseignees}/${c.total})` : ''}</TableCell>
      </TableRow>
      {a.lignes.map((l) => (
        <TableRow key={l.ligneId}>
          <TableCell>
            <Link to={`/plan/${l.ligneId}`} className="font-medium hover:underline">
              {l.libelle}
            </Link>
            <div className="text-xs text-muted-foreground">
              {l.code}
              {l.axeCode && ` · ${l.axeCode}`}
              {l.lieux.length > 0 && ` · ${l.lieux.join(', ')}`}
              {l.finPrevue && ` · fin prévue ${dateFr(l.finPrevue)}`}
            </div>
          </TableCell>
          <TableCell className="text-sm">
            {l.responsable?.nom || l.responsableLibelle || <span className="text-xs text-muted-foreground">non désigné</span>}
            {l.aParametrer && (
              <div className="text-[11px] text-amber-700">
                <Target className="mr-1 inline size-3" />à paramétrer
              </div>
            )}
          </TableCell>
          <TableCell>
            <div className="flex flex-wrap gap-1">
              <StatutPlanBadge statut={l.statut} />
              {l.retard.enRetard && (
                <Badge variant="destructive" title={l.retard.motif ?? undefined}>
                  <AlertTriangle className="size-3" /> {l.retard.jours} j
                </Badge>
              )}
            </div>
          </TableCell>
          <TableCell>
            <Avancement valeur={l.avancement} petite />
          </TableCell>
          <TableCell className="num text-right text-sm">
            {pct(l.financier.tauxExecution, 0)}
            <div className="text-xs text-muted-foreground">{montant(l.financier.paye)} / {montant(l.financier.budgetActualise)}</div>
            {l.comparaison.aExaminer && <div className="text-[11px] text-amber-700">écart à examiner ({l.comparaison.ecart! > 0 ? '+' : ''}{nombre(l.comparaison.ecart)} pts)</div>}
          </TableCell>
          <TableCell className="text-xs">
            {l.enAttente > 0 && <Badge variant="secondary">{l.enAttente} à valider</Badge>}
            {l.complementsDemandes > 0 && <Badge variant="destructive">complément demandé</Badge>}
            {l.aMettreAJour && (
              <div className="text-amber-700">
                <CalendarClock className="mr-1 inline size-3" />échéance {dateFr(l.echeance)}
              </div>
            )}
            {!l.aMettreAJour && !l.enAttente && <div className="text-muted-foreground">{l.derniereValidation ? `validée le ${dateFr(l.derniereValidation)}` : 'jamais renseignée'}</div>}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}
